import React, { useState, useEffect, useMemo } from 'react';
import RestHelper from 'utils/RestHelper';
import PropTypes from 'prop-types';
import { Spin, Table, TreeSelect, Button, Pagination, Divider, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { fromJS } from 'immutable';
import { EXCEPTION_TYPE_LIST } from 'utils/constants';
import scss from './IssueCrashPreview.scss';
import { ReportDetail } from 'components/exception/issue';
import DataDetail from 'components/exception/issue/DataDetail';
import lastReportStyle from 'components/exception/issue/LastReport/style.scss';
import { isMobile, isPcOrLinux } from 'utils/platform';
import IssueCrashSearchUtil from 'utils/IssueCrashSearchUtil';
import { IssueReducerUtil } from 'reducers/issue/issueReducer';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import { CsSelect } from 'components/antd-extension/CsSelect';
import { CS_STYLES } from 'utils/constants/style-constants';
import { LinkOutlined } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import { getIssueDetailUrlPrefix } from 'utils/helper';
import { Link } from 'react-router-dom';

const IssueRegroupCrashInfoList = (props) => {
  const {
    appId,
    platformId,
    issueId,
    linkIssueId,
    crashList,
    exceptionType, // 'crashes', 'blocks', 'errors' 类型
    showMatchReportCount,
    onClose,
  } = props;

  const pid = platformId;

  const { t } = useTranslation();

  const [isIssueExpandedLoading, setIsIssueExpandedLoading] = useState(false);
  const [expandedCrashIdList, setExpandedCrashIdList] = useState([]);
  const [matchReportCountFromCrashList, setMatchReportCountFromCrashList] = useState(0);
  const [expandedCurrentCrashIndex, setExpandedCurrentCrashIndex] = useState(0);
  const [expandedCrashImmutableIssue, setExpandedCrashImmutableIssue] = useState(fromJS({}));
  const [stackLineDetailObjectList, setStackLineDetailObjectList] = useState([]);

  async function expandIssue() {
    setIsIssueExpandedLoading(true);
    await fetchExpandedCrash(crashList[0].crashId)
    console.log("拿到的crashIdList", crashList)
    const expandedCrashIdList = crashList.map(x => x.crashId);
    setExpandedCrashIdList(expandedCrashIdList);
    setMatchReportCountFromCrashList(expandedCrashIdList.length);
    setExpandedCurrentCrashIndex(0);
    setStackLineDetailObjectList(crashList[0].stackLineDetailObjectList);
    setIsIssueExpandedLoading(false);
  }

  useEffect(() => {
    expandIssue();
  }, []);

  async function fetchExpandedCrash(crashId) {
    setIsIssueExpandedLoading(true);
    const rsp = await RestHelper.get(`/crashDoc/appId/${appId}/platformId/${pid}/crashHash/${crashId}`);
    // 切换到新的crash之前，先清空上一个crash残留的自定义kv合并中间状态，避免串数据
    const clearedIssue = IssueReducerUtil.clearCustomKvMergeState(expandedCrashImmutableIssue);
    let tempGlobalIssueState = fromJS({}).set('current', clearedIssue);
    tempGlobalIssueState = IssueReducerUtil.handleGetCrashDoc(tempGlobalIssueState, { response: rsp.json.ret, params: { pid } });
    if (isMobile(platformId)) {
      const rsp = await RestHelper.get(`/appDetailCrash/appId/${appId}/platformId/${pid}/crashHash/${crashId}`);
      tempGlobalIssueState = IssueReducerUtil.handleGetCrashAttachment(tempGlobalIssueState, { response: rsp.json.ret, params: { pid } });
    }
    const newImmutableIssue = tempGlobalIssueState.get('current');
    setExpandedCrashImmutableIssue(newImmutableIssue);
    setIsIssueExpandedLoading(false);
  }

  const crashIdOptions = expandedCrashIdList.map((x) => {
    return {
      label: x,
      value: x,
    };
  });

  const expandedCurrentCrashId = expandedCrashIdList[expandedCurrentCrashIndex];

  useEffect(() => {
    if (!expandedCurrentCrashId) {
      return;
    }
    fetchExpandedCrash(expandedCurrentCrashId);
  }, [expandedCurrentCrashId]);

  useEffect(() => {
    setStackLineDetailObjectList(crashList[expandedCurrentCrashIndex].stackLineDetailObjectList);
  }, [expandedCurrentCrashIndex]);

  const issueDetailUrlPrefix = getIssueDetailUrlPrefix(exceptionType);


  return <div className={scss.crashDetail}>
    <Spin spinning={isIssueExpandedLoading}>
      <div className={scss.crashDetailHeader} style={{ fontSize: '14px', margin: '0px'}}>
        <div style={{ marginRight: '12px' }}>Crash ID:
          <CsSelect
            style={{ marginLeft: '8px', width: '320px' }}
            options={crashIdOptions}
            value={expandedCurrentCrashId}
            onChange={(v) => {
              const i = expandedCrashIdList.indexOf(v);
              if (i >= 0) {
                setExpandedCurrentCrashIndex(i);
              }
            }}
          />
        </div>
        <Button
          type='link'
          style={{ marginRight: '12px', padding: 0 }}
        >
          <div className={scss.IconHover} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <LinkOutlined />
            <Link
              to={`/${issueDetailUrlPrefix}/${appId}/${linkIssueId}/report-id/${expandedCurrentCrashId}?pid=${pid}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {ze("跳转到上报", "Open the Report")}
            </Link>
          </div>
        </Button>
        <Pagination
          size='small'
          total={matchReportCountFromCrashList}
          showTotal={() => `${expandedCurrentCrashIndex + 1} / ${matchReportCountFromCrashList}`}
          pageSize={1}
          showSizeChanger={false}
          current={expandedCurrentCrashIndex + 1}
          onChange={(pageNum) => setExpandedCurrentCrashIndex(pageNum - 1)}
        />
        { showMatchReportCount
          && <div>{ t('SENIORSEARCH.crash结果统计', { totalCrashMatchCount: matchReportCountFromCrashList }) }</div> }
        <div style={{ flexGrow: '1' }}/>
      </div>
      <Divider
        style={{ margin: '8px 0', color: '#AAA' }}
        orientation='left'
        plain={true}
      >{ t('LASTREPORT.上报信息') }</Divider>
      { expandedCurrentCrashId && <div>
        <ReportDetail
          appId={appId}
          pid={platformId}
          exceptionType={exceptionType}
          issue={expandedCrashImmutableIssue}
          isSmallSize={true}
        />
      </div>}
      <Divider
        style={{ margin: '8px 0', color: '#AAA' }}
        orientation='left'
        plain={true}
      >{ t('LASTREPORT.errorStack') }</Divider>
      { expandedCurrentCrashId && <div>
        <DataDetail
          pid={platformId}
          dataType='stack'
          style={lastReportStyle}
          exceptionType={exceptionType}
          issue={expandedCrashImmutableIssue}
          stackLineDetailObjectList={stackLineDetailObjectList}
        />
      </div>}
    </Spin>
  </div>;
}

IssueRegroupCrashInfoList.propTypes = {
  appId: PropTypes.string.isRequired,
  platformId: PropTypes.string.isRequired,
  issueId: PropTypes.string.isRequired,
  linkIssueId: PropTypes.string.isRequired,
  crashList: PropTypes.array,
  exceptionType: PropTypes.string.isRequired,
  showMatchReportCount: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default IssueRegroupCrashInfoList;
